import React from 'react'
import { getTomorrowStart } from '../../helpers/dateDefaults'
import { OrderItem } from '../../types/Order'
import { DeliveryProvider } from '../../types/DeliveryProviders'
import { NewOrderItem } from '../../helpers/mapToOrderItem'

// Hook for customer form state
export function useCustomerForm() {
  const [customerName, setCustomerName] = React.useState('')
  const [customerPhone, setCustomerPhone] = React.useState('')
  const [customerEmail, setCustomerEmail] = React.useState('')
  const [isEmailValid, setIsEmailValid] = React.useState(true)
  const [isPhoneValid, setIsPhoneValid] = React.useState(true)

  const handleInputChange = (field: string, value: string) => {
    if (field === 'name') setCustomerName(value)
    if (field === 'phone') setCustomerPhone(value)
    if (field === 'email') setCustomerEmail(value)
  }

  const reset = () => {
    setCustomerName('')
    setCustomerPhone('')
    setCustomerEmail('')
    setIsEmailValid(true)
    setIsPhoneValid(true)
  }

  return {
    customerName,
    customerPhone,
    customerEmail,
    isEmailValid,
    isPhoneValid,
    setIsEmailValid,
    setIsPhoneValid,
    handleInputChange,
    reset,
  }
}

// Hook for delivery form state
export function useDeliveryForm() {
  const [deliveryDate, setDeliveryDate] = React.useState<Date>(getTomorrowStart())
  const [deliveryTimeSlot, setDeliveryTimeSlot] = React.useState<string | undefined>(undefined)
  const [deliveryTimeSlotError, setDeliveryTimeSlotError] = React.useState(false)
  const [deliveryAddress, setDeliveryAddress] = React.useState('')
  const [zipCode, setZipCode] = React.useState('')
  const [city, setCity] = React.useState('')
  const [deliveryInformationError, setDeliveryInformationError] = React.useState(false)
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = React.useState<DeliveryProvider | null>(null)
  const [deliveryMethodError, setDeliveryMethodError] = React.useState(false)

  const onDeliverySelection = (provider: DeliveryProvider) => {
    setDeliveryMethodError(false)
    setSelectedDeliveryMethod(provider)
  }

  const reset = () => {
    setDeliveryDate(getTomorrowStart())
    setDeliveryTimeSlot(undefined)
    setDeliveryTimeSlotError(false)
    setDeliveryAddress('')
    setZipCode('')
    setCity('')
    setDeliveryInformationError(false)
    setSelectedDeliveryMethod(null)
    setDeliveryMethodError(false)
  }

  return {
    deliveryDate,
    deliveryTimeSlot,
    deliveryTimeSlotError,
    deliveryAddress,
    zipCode,
    city,
    deliveryInformationError,
    selectedDeliveryMethod,
    deliveryMethodError,
    setDeliveryDate,
    setDeliveryTimeSlot,
    setDeliveryTimeSlotError,
    setDeliveryAddress,
    setZipCode,
    setCity,
    setDeliveryInformationError,
    setSelectedDeliveryMethod,
    setDeliveryMethodError,
    onDeliverySelection,
    reset,
  }
}

// Hook for products management
export function useProductsManagement() {
  const [productsToAdd, setProductsToAdd] = React.useState<OrderItem[]>([])
  const [productToAdd, setProductToAdd] = React.useState<NewOrderItem | null>(null)
  const [productToUpdate, setProductToUpdate] = React.useState<OrderItem | null>(null)
  const [showAddProduct, setShowAddProduct] = React.useState(false)
  const [updateProduct, setUpdateProduct] = React.useState(false)
  const [addProductError, setAddProductError] = React.useState(false)

  const toggleEditProduct = (item: OrderItem) => {
    setProductToUpdate({ ...item })
    if (showAddProduct) setShowAddProduct(false)
    if (!updateProduct) setUpdateProduct(true)
  }

  const handleDeleteProduct = (id: string) => {
    setProductsToAdd(prev => prev.filter(p => p.id !== id))
    if (productToUpdate?.id === id) {
      setProductToUpdate(null)
      setUpdateProduct(false)
    }
  }

  const reset = () => {
    setProductsToAdd([])
    setProductToAdd(null)
    setProductToUpdate(null)
    setShowAddProduct(false)
    setUpdateProduct(false)
    setAddProductError(false)
  }

  return {
    productsToAdd,
    productToAdd,
    productToUpdate,
    showAddProduct,
    updateProduct,
    addProductError,
    setProductsToAdd,
    setProductToAdd,
    setProductToUpdate,
    setShowAddProduct,
    setUpdateProduct,
    setAddProductError,
    toggleEditProduct,
    handleDeleteProduct,
    reset,
  }
}

// Hook for comments
export function useComments() {
  const [storeComment, setStoreComment] = React.useState('')
  const [customerComment, setCustomerComment] = React.useState('')

  const reset = () => {
    setStoreComment('')
    setCustomerComment('')
  }

  return {
    storeComment,
    customerComment,
    setStoreComment,
    setCustomerComment,
    reset,
  }
}

// Hook for form validation state
export function useFormValidation() {
  const [showValidation, setShowValidation] = React.useState(false)
  const [createOrderError, setCreateOrderError] = React.useState<string | null>(null)

  const reset = () => {
    setShowValidation(false)
    setCreateOrderError(null)
  }

  return {
    showValidation,
    createOrderError,
    setShowValidation,
    setCreateOrderError,
    reset,
  }
}

// Hook for modal state
export function useModalState() {
  const [showUnsavedModal, setShowUnsavedModal] = React.useState(false)
  const [isCreatingOrder, setIsCreatingOrder] = React.useState(false)

  return {
    showUnsavedModal,
    isCreatingOrder,
    setShowUnsavedModal,
    setIsCreatingOrder,
  }
}